import { Link } from 'react-router-dom'
import { useState } from 'react'
import {
  MessageCircle,
  ChevronDown,
  ChevronUp,
  Brain,
  MessagesSquare,
  TrendingUp,
  Users,
  Zap,
  CheckCircle2,
  Menu,
  X,
  ArrowRight,
} from 'lucide-react'
import PricingSection from '@/components/home/PricingSection'
import FloatingWhatsAppButton from '@/components/home/FloatingWhatsAppButton'
import HeroPhoneMockup from '@/components/home/HeroPhoneMockup'
import BetaCountdownStrip from '@/components/home/BetaCountdownStrip'
import { buildFaqJsonLd, serializeJsonLd } from '@/components/home/homeSeo'
import { getWhatsAppSalesUrl } from '@/utils/contactLinks'

export const FAQ_ITEMS = [
  {
    question: '¿Qué canales de mensajería soporta IridiaChat?',
    answer:
      'IridiaChat es una plataforma multicanal diseñada para WhatsApp Business Cloud API (conexión oficial de Meta y modo Coexistencia), Telegram y WebChat. Conecta tus canales y gestiónalos todos desde un solo lugar con bots autónomos y tu equipo.',
  },
  {
    question: '¿Puedo usar el número actual de WhatsApp de mi negocio?',
    answer:
      'Sí. Puedes vincular tu número de WhatsApp comercial actual en minutos mediante el Registro Insertado Oficial de Meta Cloud API (Modo Coexistencia), sin perder tus chats ni tus contactos existentes.',
  },
  {
    question: '¿Cómo funciona la sincronización automática con Google Sheets o ERP?',
    answer:
      'Solo necesitas conectar el enlace de tu Google Sheet o sistema administrativo. El asistente cotiza con existencias reales y descuenta automáticamente el inventario al concretar pedidos por WhatsApp.',
  },
  {
    question: '¿Cómo me registro si la plataforma se encuentra en Beta Privada?',
    answer:
      'Durante nuestra fase de Beta Privada, el acceso incluye acompañamiento directo y activación asistida. Puedes solicitar tu acceso haciendo clic en "7 Días de Prueba" o solicitándolo directamente por WhatsApp.',
  },
  {
    question: '¿Qué métodos de pago aceptan en Venezuela e internacionalmente?',
    answer:
      'Por ahora aceptamos pagos en Bolívares mediante Pago Móvil y transferencia bancaria a la tasa oficial del Banco Central de Venezuela (BCV), sin recargos. Más adelante habilitaremos PayPal, tarjeta de débito y Binance Pay.',
  },
  {
    question: '¿Qué ocurre cuando un cliente necesita atención humana o desea cerrar la compra?',
    answer:
      'El asistente detecta la confirmación de compra o la solicitud de asesor y transfiere la conversación de inmediato a tu equipo humano con el resumen completo de productos cotizados para que cobren y despachen con rapidez.',
  },
]

export const METRICS = [
  { value: '+50.000', label: 'Mensajes procesados por mes', icon: MessagesSquare },
  { value: '35%', label: 'Recuperación de cotizaciones', icon: TrendingUp },
  { value: '< 500 ms', label: 'Tiempo de respuesta con IA', icon: Zap },
  { value: '100%', label: 'Reglas de negocio e inventario en vivo', icon: CheckCircle2 },
]

export const TESTIMONIALS = [
  {
    quote:
      'El asistente cotiza y atiende a clientes en paralelo. Nuestro ticket promedio subió y el equipo de mostrador ya no colapsa escribiendo los mismos precios 100 veces al día.',
    name: 'Carlos Mendoza',
    role: 'Gerente Comercial',
    company: 'Distribuidora Automotriz Caracas',
    tag: 'Tienda de Repuestos',
  },
  {
    quote:
      'En horas pico de almuerzo antes perdíamos clientes por demoras en responder. Ahora IridiaChat toma la orden, suma adicionales y cocina empieza a preparar de inmediato.',
    name: 'Valeria Rivas',
    role: 'Directora de Operaciones',
    company: 'Burger Spot & Delivery',
    tag: 'Gastronomía & Restaurante',
  },
  {
    quote:
      'Nos ahorra horas respondiendo requisitos y ubicación. Los pacientes agendan su turno tentativo y recepción solo valida el horario, con total tranquilidad clínica.',
    name: 'Dr. Arnaldo Parra',
    role: 'Coordinador Médico',
    company: 'Centro Médico Quirúrgico',
    tag: 'Salud & Especialidades',
  },
]

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index)
  }

  const closeMobileMenu = () => {
    setMobileMenuOpen(false)
  }

  return (
    <div className="min-h-screen bg-white w-full text-slate-800 antialiased selection:bg-indigo-100 selection:text-indigo-900">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: serializeJsonLd(buildFaqJsonLd(FAQ_ITEMS)) }}
      />
      <BetaCountdownStrip />

      {/* Header/Navbar */}
      <header className="bg-white/90 backdrop-blur-md sticky top-0 z-50 w-full border-b border-slate-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group">
              <img
                src="/new/IridiaChat_Logo_Fondo_Claro_Transparente.png"
                alt="IridiaChat"
                className="h-8 sm:h-9 w-auto object-contain transition-transform group-hover:scale-105"
              />
              <span className="sr-only">IridiaChat</span>
              <span className="px-2 py-0.5 text-[10px] font-black tracking-wider uppercase bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-full">
                BETA
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8 text-sm font-medium">
              <a href="#diferenciales" className="text-slate-600 hover:text-indigo-600 transition-colors">
                ¿Por qué IridiaChat?
              </a>
              <a href="#planes" className="text-slate-600 hover:text-indigo-600 transition-colors">
                Planes y Precios
              </a>
              <a href="#faq" className="text-slate-600 hover:text-indigo-600 transition-colors">
                Preguntas Frecuentes
              </a>
              <Link to="/login" className="text-slate-700 hover:text-indigo-600 transition-colors font-semibold">
                Iniciar Sesión
              </Link>
              <Link
                to="/register"
                className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm transition-all shadow-sm hover:shadow-md"
              >
                7 Días de Prueba
              </Link>
            </nav>

            {/* Mobile / Tablet Menu Button */}
            <div className="lg:hidden flex items-center gap-2">
              <Link
                to="/register"
                className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700 transition"
              >
                7 Días de Prueba
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition focus:outline-none"
                aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-6 h-6 text-slate-900" /> : <Menu className="w-6 h-6 text-slate-900" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Dropdown Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-b border-slate-100 px-4 pt-3 pb-6 space-y-3 shadow-xl">
            <nav className="flex flex-col space-y-2 text-sm font-medium">
              <a
                href="#diferenciales"
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-lg text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition"
              >
                ¿Por qué IridiaChat?
              </a>
              <a
                href="#planes"
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-lg text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition"
              >
                Planes y 7 Días de Prueba
              </a>
              <a
                href="#faq"
                onClick={closeMobileMenu}
                className="px-3 py-2 rounded-lg text-slate-700 hover:bg-indigo-50 hover:text-indigo-700 transition"
              >
                Preguntas Frecuentes
              </a>
              <div className="pt-2 border-t border-slate-100 flex flex-col gap-2">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="w-full text-center py-2.5 rounded-xl border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 transition"
                >
                  Iniciar Sesión
                </Link>
                <Link
                  to="/register"
                  onClick={closeMobileMenu}
                  className="w-full text-center py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition shadow-sm"
                >
                  Registrarse · 7 Días de Prueba
                </Link>
                <a
                  href={getWhatsAppSalesUrl('Hola IridiaChat, deseo solicitar información sobre la prueba gratuita o demo.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={closeMobileMenu}
                  className="w-full text-center py-2.5 rounded-xl bg-indigo-50 text-indigo-800 border border-indigo-200 font-semibold hover:bg-indigo-100 transition flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-600" />
                  <span>Chatear por WhatsApp</span>
                </a>
              </div>
            </nav>
          </div>
        )}
      </header>

      <main>
      {/* Hero Section */}
      <section className="relative pt-12 pb-16 lg:pt-20 lg:pb-24 overflow-hidden bg-gradient-to-b from-slate-50/70 via-white to-white">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
            {/* Left Column: Value Prop & CTAs */}
            <div className="lg:col-span-7 text-center lg:text-left">
              {/* Pill Badge */}
              <div className="inline-flex items-center gap-2 bg-indigo-50 border border-indigo-200 text-indigo-900 px-3.5 py-1.5 rounded-full text-xs font-semibold mb-6">
                <span className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse" />
                <span>BETA PRIVADA · 7 días de prueba con 400 créditos gratis</span>
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black text-slate-900 tracking-tight leading-[1.12] mb-6">
                Ventas por WhatsApp con <span className="text-indigo-600">criterio comercial</span> y stock en vivo.
              </h1>

              <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Olvídate de los bots rígidos de "marque 1 o 2". IridiaChat entiende consultas cotidianas de tus clientes, responde con tu inventario real según las reglas de tu negocio y entrega los pedidos listos a tu equipo de ventas.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3.5 justify-center lg:justify-start mb-6">
                <Link
                  to="/register"
                  className="px-6 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-base transition-all shadow-md shadow-indigo-600/20 flex items-center justify-center gap-2"
                >
                  <span>Probar 7 Días Gratis</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href={getWhatsAppSalesUrl('Hola IridiaChat, deseo probar la demo en vivo en WhatsApp.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3.5 rounded-xl bg-white hover:bg-slate-50 text-slate-800 border border-slate-200 font-semibold text-base transition-all shadow-xs flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                  <span>Ver Demo en WhatsApp</span>
                </a>
              </div>

              {/* Minimal Trust Checklist */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-y-2 gap-x-6 text-xs text-slate-500 font-medium pt-2">
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                  Sin tarjeta de crédito requerida
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                  Usa tu propio número comercial
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-indigo-600" />
                  Adaptado a las reglas de tu negocio
                </span>
              </div>
            </div>

            {/* Right Column: Hero Live Mockup */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end w-full">
              <HeroPhoneMockup />
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators Section - ¿Por qué no somos un chatbot común? */}
      <section id="diferenciales" className="py-16 lg:py-24 bg-white border-t border-slate-100 scroll-mt-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold uppercase tracking-wider text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200 mb-3 inline-block">
              QUÉ NOS HACE DIFERENTES
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Diseñado para cerrar ventas, no para enviar respuestas genéricas
            </h2>
            <p className="text-slate-600 text-base sm:text-lg">
              La mayoría de chatbots frustran a los compradores con menús rígidos. IridiaChat atiende con la agilidad y el criterio de tu mejor vendedor de mostrador.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-7 transition-all duration-300 hover:bg-white hover:border-indigo-300 hover:shadow-lg hover:shadow-slate-100 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-indigo-100/70 text-indigo-700 rounded-xl flex items-center justify-center mb-5">
                  <Brain className="w-6 h-6" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 mb-2">
                  CONVERSACIÓN NATURAL
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Entendimiento comercial sin menús
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Entiende mensajes en ráfaga, fotos de productos y preguntas cotidianas. Sabe de qué producto hablan en todo momento y guía con fluidez sin hacer navegar al cliente por menús de opciones.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-slate-200/60 flex items-center text-xs font-semibold text-indigo-700">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Cero respuestas enlatadas o desorientadas
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-7 transition-all duration-300 hover:bg-white hover:border-indigo-300 hover:shadow-lg hover:shadow-slate-100 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-indigo-100/70 text-indigo-700 rounded-xl flex items-center justify-center mb-5">
                  <Zap className="w-6 h-6" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 mb-2">
                  INVENTARIO & REGLAS PROPIAS
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Venta directa con tus reglas e inventario
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  El asistente se adapta a cómo vende tu tienda: consulta existencias reales en tiempo real y aplica tus monedas de cobro, condiciones de entrega, descuentos y políticas comerciales configuradas por ti.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-slate-200/60 flex items-center text-xs font-semibold text-indigo-700">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Cero desajustes de stock ni respuestas fuera de política
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-slate-50/60 border border-slate-200/80 rounded-2xl p-7 transition-all duration-300 hover:bg-white hover:border-indigo-300 hover:shadow-lg hover:shadow-slate-100 flex flex-col justify-between">
              <div>
                <div className="w-12 h-12 bg-indigo-100/70 text-indigo-700 rounded-xl flex items-center justify-center mb-5">
                  <Users className="w-6 h-6" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-indigo-700 mb-2">
                  COLA DE ATENCIÓN EN EQUIPO
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Un solo número atendido por todo tu equipo
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  No busca sustituir a tus vendedores: organiza la atención en una cola de mensajes compartida y, cuando el cliente confirma la compra, entrega la conversación a tu equipo con los productos y datos listos para coordinar el cobro.
                </p>
              </div>
              <div className="pt-6 mt-6 border-t border-slate-200/60 flex items-center text-xs font-semibold text-indigo-700">
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Bandeja compartida y cola de atención para tu equipo
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section (Prominente y con Trial de 7 días) */}
      <PricingSection />

      {/* Minimalist FAQ Section */}
      <section id="faq" className="py-16 lg:py-24 bg-slate-50/60 border-t border-slate-200/80 scroll-mt-16">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 bg-emerald-100/60 px-3 py-1 rounded-full border border-emerald-200 mb-3 inline-block">
              DUDAS FRECUENTES
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Preguntas Frecuentes
            </h2>
            <p className="text-slate-600 text-base sm:text-lg mt-3">
              Todo lo que necesitas saber antes de comenzar tu prueba gratuita.
            </p>
          </div>

          <div className="space-y-3.5">
            {FAQ_ITEMS.map((faq, index) => {
              const isOpen = openFaq === index
              return (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-slate-200/90 shadow-2xs overflow-hidden transition-all duration-200"
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(index)}
                    className="w-full px-5 py-4 flex justify-between items-center text-left hover:bg-slate-50/60 transition-colors gap-4"
                    aria-expanded={isOpen}
                    aria-controls={`faq-answer-${index}`}
                    id={`faq-question-${index}`}
                  >
                    <span className="font-bold text-slate-900 text-sm sm:text-base">
                      {faq.question}
                    </span>
                    <span className="p-1 rounded-md bg-slate-100 text-slate-600 shrink-0">
                      {isOpen ? (
                        <ChevronUp className="w-4 h-4 text-emerald-600" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-slate-500" />
                      )}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      id={`faq-answer-${index}`}
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                      className="px-5 pb-4 pt-1 text-slate-600 text-xs sm:text-sm leading-relaxed border-t border-slate-100 bg-slate-50/40"
                    >
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Modern Bottom CTA Card */}
      <section className="py-16 bg-white border-t border-slate-100">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <div className="bg-slate-900 text-white rounded-3xl p-8 sm:p-12 text-center shadow-xl relative overflow-hidden">
            <div className="max-w-2xl mx-auto relative z-10">
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 uppercase tracking-wider mb-4 inline-block">
                ACTIVACIÓN EN MINUTOS
              </span>
              <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight mb-4">
                Empieza a vender por WhatsApp y Redes con IA
              </h2>
              <p className="text-slate-300 text-sm sm:text-base mb-8">
                Crea tu cuenta hoy y disfruta de 7 días de prueba con 400 créditos para validar ventas reales con tus clientes.
              </p>
              <div className="flex flex-col sm:flex-row gap-3.5 justify-center items-center">
                <Link
                  to="/register"
                  className="w-full sm:w-auto px-7 py-3.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm transition-all shadow-md shadow-indigo-600/25"
                >
                  Empieza ya · 7 Días de Prueba
                </Link>
                <a
                  href={getWhatsAppSalesUrl('Hola IridiaChat, deseo solicitar información sobre cómo empezar con mi negocio.')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto px-6 py-3.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-semibold text-sm transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4 text-emerald-400" />
                  <span>Hablar con Asesor</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      </main>

      {/* Floating WhatsApp Action Button */}
      <FloatingWhatsAppButton />

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 border-t border-slate-900 w-full text-xs">
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-10">
            {/* Columna 1: Marca y operador */}
            <div className="md:col-span-2 space-y-3">
              <div className="flex items-center gap-2">
                <img
                  src="/new/IridiaChat_Logo_Fondo_Oscuro_Transparente.png"
                  alt="IridiaChat"
                  className="h-8 w-auto object-contain"
                />
                <span className="sr-only">IridiaChat</span>
                <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 rounded-full">
                  BETA
                </span>
              </div>
              <p className="text-slate-400 text-xs max-w-md leading-relaxed">
                Plataforma SaaS de automatización conversacional y ventas con WhatsApp Business Platform (Meta Tech Provider).
              </p>
              <div className="space-y-1 bg-slate-900 p-3 rounded-xl border border-slate-800 max-w-md text-[11px]">
                <div className="text-indigo-400 font-semibold uppercase tracking-wider text-[10px]">
                  Operador Legal y Fiscal
                </div>
                <div>
                  <span className="text-slate-400">Razón Social:</span>{' '}
                  <strong className="text-white">INVERSIONES MERCADO NARANJA, C.A.</strong>
                </div>
                <div>
                  <span className="text-slate-400">RIF:</span>{' '}
                  <span className="text-indigo-300 font-mono font-semibold">J-502314539</span>
                  <span className="text-slate-600 mx-2">·</span>
                  <span className="text-slate-400">Caracas, Venezuela</span>
                </div>
              </div>
            </div>

            {/* Columna 2: Plataforma */}
            <div>
              <h3 className="text-xs font-semibold text-white tracking-wider uppercase mb-3">
                Plataforma
              </h3>
              <ul className="space-y-2">
                <li>
                  <a href="#diferenciales" className="hover:text-indigo-400 transition-colors">
                    ¿Por qué IridiaChat?
                  </a>
                </li>
                <li>
                  <a href="#planes" className="hover:text-indigo-400 transition-colors">
                    Planes y 7 Días de Prueba
                  </a>
                </li>
                <li>
                  <a href="#faq" className="hover:text-indigo-400 transition-colors">
                    Preguntas Frecuentes
                  </a>
                </li>
                <li>
                  <Link to="/login" className="hover:text-indigo-400 transition-colors">
                    Iniciar Sesión
                  </Link>
                </li>
                <li>
                  <Link to="/register" className="hover:text-indigo-400 transition-colors">
                    Registrarse (7 Días de Prueba)
                  </Link>
                </li>
              </ul>
            </div>

            {/* Columna 3: Legal y Cumplimiento Meta */}
            <div>
              <h3 className="text-xs font-semibold text-white tracking-wider uppercase mb-3">
                Legal y Cumplimiento
              </h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/privacidad" className="hover:text-indigo-400 transition-colors">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/terminos" className="hover:text-indigo-400 transition-colors">
                    Términos de Servicio
                  </Link>
                </li>
                <li>
                  <Link to="/eliminacion-datos" className="hover:text-indigo-400 transition-colors">
                    Eliminación de Datos
                  </Link>
                </li>
                <li className="pt-1">
                  <a
                    href={getWhatsAppSalesUrl('Hola IridiaChat, deseo contactar con soporte comercial.')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-emerald-400 transition-colors flex items-center gap-1.5 text-emerald-300"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>WhatsApp Comercial</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-6 border-t border-slate-900 flex flex-col sm:flex-row justify-between items-center gap-3 text-slate-500 text-[11px]">
            <div>
              © {new Date().getFullYear()} IridiaChat (iridiachat.com) · Operado por <strong>INVERSIONES MERCADO NARANJA, C.A.</strong>
            </div>
            <div>
              Meta Tech Provider Integration
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}
